import { useEffect, useRef } from "react";
import { useReducedMotion } from "@/lib/useReducedMotion";

// Vidéo pilotée au scroll : currentTime lié à la progression du défilement.
// prefers-reduced-motion: reduce -> on ne scrube pas, l'affiche fixe reste.
export const ScrollVideo = ({ src, poster, children, heightVh = 200, overlay = true }) => {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce) return () => {};
    const video = videoRef.current;
    const wrap = wrapRef.current;
    if (!video || !wrap) return () => {};
    let duration = 0;
    let raf = 0;
    const onMeta = () => { duration = video.duration || 0; };
    video.addEventListener("loadedmetadata", onMeta);
    if (video.readyState >= 1) onMeta();

    const update = () => {
      raf = 0;
      if (!duration) return;
      const rect = wrap.getBoundingClientRect();
      const total = wrap.offsetHeight - window.innerHeight;
      const progress = Math.min(1, Math.max(0, -rect.top / (total || 1)));
      const t = progress * duration;
      if (Math.abs(video.currentTime - t) > 0.03) {
        try { video.currentTime = t; } catch (e) {}
      }
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
    return () => {
      video.removeEventListener("loadedmetadata", onMeta);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reduce, src]);

  return (
    <section ref={wrapRef} style={{ height: `${heightVh}vh` }} className="relative bg-black">
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          muted
          playsInline
          preload="auto"
          tabIndex={-1}
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {overlay && <div className="absolute inset-0 bg-gradient-to-t from-black via-black/35 to-black/25" />}
        {children}
      </div>
    </section>
  );
};
